import cx from 'classnames';
import Menu, { Divider, MenuItem, SubMenu } from 'rc-menu';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setPathLocale, SITE_LANGUAGES } from '../../../common/localeShared';
import { setCurrentLocale } from '../../libs/utils';
import { t } from 'i18next';
import Cookies from 'js-cookie';
import { canUseDOM } from 'exenv';
import { COOKIES_ENABLED_KEY } from '../presentational/cookieFooter';
import { menuBuiltinPlacements } from '../menu/menuHelper';

type TSiteLanguageSelectorProps = {
  includePlug?: boolean;
  useBuiltinPlacements?: boolean;
};

const FAVORITE_LANGUAGE_COOKIE_NAME = 'rio-language';
const SET_LOCALE_CONFIG_CODE = 'SET_LOCALE_CONFIG_CODE';

export function SiteLanguageSelector(props: TSiteLanguageSelectorProps) {
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const cookiesAccepted = canUseDOM ? Cookies.get(COOKIES_ENABLED_KEY) : undefined;
  const favoriteLang = canUseDOM ? Cookies.get(FAVORITE_LANGUAGE_COOKIE_NAME) || '' : '';
  const normalizedI18nLanguage = i18n.language.toLowerCase().split('-')[0];
  const preferredLang = favoriteLang
    ? SITE_LANGUAGES.find((lang) => lang.i18nextCode === favoriteLang || lang.sitePathRoot === favoriteLang) || null
    : null;
  const bestLang =
    preferredLang ||
    SITE_LANGUAGES.find(
      (lang) =>
        lang.i18nextCode === i18n.language ||
        lang.sitePathRoot === i18n.language ||
        lang.i18nextCode === normalizedI18nLanguage
    ) ||
    null;

  function handleLanguageMenuClick(params: any) {
    const { key } = params;
    if (key.indexOf('https://') === 0) {
      window.open(key, '_blank');
      return;
    }

    RIO_TRACK_EVENT('event', 'select_content', {
      content_type: 'choose_language',
      content_id: key
    });

    Cookies.set(FAVORITE_LANGUAGE_COOKIE_NAME, key, { expires: 365 * 10 });
    setCurrentLocale(key);
    window.location.pathname = setPathLocale(window.location.pathname, key);
    dispatch({
      type: SET_LOCALE_CONFIG_CODE,
      payload: key
    });
  }

  useEffect(() => {
    if (!cookiesAccepted) {
      return;
    }

    // Only force language from an explicit persisted preference.
    if (!preferredLang) {
      return;
    }

    if (normalizedI18nLanguage === preferredLang.i18nextCode) {
      return;
    }

    const nextPath = setPathLocale(window.location.pathname, preferredLang.i18nextCode);
    if (nextPath === window.location.pathname) {
      return;
    }

    window.location.pathname = nextPath;
    setCurrentLocale(preferredLang.i18nextCode);
    dispatch({
      type: SET_LOCALE_CONFIG_CODE,
      payload: preferredLang.i18nextCode
    });
  }, [cookiesAccepted, dispatch, i18n.language, preferredLang]);

  return (
    <Menu
      onClick={handleLanguageMenuClick}
      selectable={false}
      mode="horizontal"
      disabledOverflow={true}
      builtinPlacements={props.useBuiltinPlacements ? menuBuiltinPlacements : undefined}
      className="no-background slds-p-vertical_none"
    >
      <SubMenu
        key="site-language-selector"
        title={<i className="fa fa-earth-americas" />}
        className="slds-p-vertical_none rio-menu--small-buttons"
        popupClassName="rio-menu-popup--small-width"
      >
        {SITE_LANGUAGES.map((lang) => (
          <MenuItem
            key={lang.i18nextCode}
            className={cx(
              'slds-text-align--left slds-p-vertical_none',
              bestLang?.i18nextCode === lang.i18nextCode ? 'text-bold text-raiderio' : ''
            )}
          >
            <div className="slds-grid slds-grid--align-space">
              <div className="slds-size--1-of-1">
                {bestLang?.i18nextCode === lang.i18nextCode ? (
                  <i className="fa fa-fw fa-check slds-m-right--xx-small" />
                ) : (
                  <i className="fa fa-fw slds-m-right--xx-small" />
                )}
                {lang.nativeName}
              </div>
            </div>
          </MenuItem>
        ))}

        {props.includePlug && <Divider />}
        {props.includePlug && (
          <MenuItem
            className="text-bold"
            key="https://docs.google.com/forms/d/e/1FAIpQLSd0uKD97lQQ_FUfBeduOoLgoHd_hBlkssZeeb3IgqFVR-K2Fg/viewform?usp=sf_link"
          >
            <i className="fa fa-fw fa-language slds-m-right--xx-small" /> {t('common:improve-translations')}
          </MenuItem>
        )}
      </SubMenu>
    </Menu>
  );
}
